import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { canEditInvoiceLogo } from "../../components/nav";
import {
  getInvoiceLogo,
  getInvoiceLogoImage,
  invoiceLogoErrorText,
  removeInvoiceLogo,
  setInvoiceLogo,
} from "../../api/invoiceLogo";
import { shrinkLogo } from "./invoiceLogoImage";

/**
 * Edit profile › Invoice logo (owner 2026-10-29): the logo printed on this
 * operator's invoices, beside its legal and trade name. Not the portal logo.
 * The Owner or Admin changes it; Billing and superadmin see it.
 */
export function InvoiceLogoCard({ namespace }: { namespace: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const canEdit = canEditInvoiceLogo(identity.roles);
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ severity: "success" | "error"; text: string } | null>(null);
  const [src, setSrc] = useState<string | null>(null);

  const meta = useQuery({
    queryKey: ["invoiceLogo", namespace],
    queryFn: ({ signal }) => getInvoiceLogo(identity, namespace, signal),
    enabled: !!namespace,
  });
  const sha = meta.data?.sha256 ?? "";
  const image = useQuery({
    queryKey: ["invoiceLogoImage", namespace, sha],
    queryFn: ({ signal }) => getInvoiceLogoImage(identity, namespace, signal),
    enabled: !!sha,
  });
  useEffect(() => {
    if (!image.data) {
      setSrc(null);
      return;
    }
    const url = URL.createObjectURL(image.data);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [image.data]);

  async function run(action: () => Promise<string>) {
    if (busy) return;
    setBusy(true);
    try {
      const text = await action();
      await qc.invalidateQueries({ queryKey: ["invoiceLogo", namespace] });
      setNotice({ severity: "success", text });
    } catch (err) {
      setNotice({ severity: "error", text: invoiceLogoErrorText(err) });
    } finally {
      setBusy(false);
    }
  }

  const choose = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // the same file can be chosen again
    if (!file) return;
    void run(async () => {
      await setInvoiceLogo(identity, namespace, await shrinkLogo(file));
      return "Invoice logo saved. New invoices print it.";
    });
  };

  const remove = () =>
    void run(async () => {
      await removeInvoiceLogo(identity, namespace);
      return "Invoice logo removed. New invoices print no logo.";
    });

  const has = !!meta.data;
  return (
    <Paper sx={{ p: 2, mb: 2 }} data-testid="invoice-logo-card">
      <Stack spacing={1.5}>
        <Typography variant="subtitle1" component="h2">Invoice logo</Typography>
        {notice && <Alert severity={notice.severity}>{notice.text}</Alert>}
        {meta.isError && <Alert severity="warning">{invoiceLogoErrorText(meta.error)}</Alert>}
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box sx={{ width: 200, height: 80, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
            {src ? (
              <img src={src} alt="Invoice logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            ) : (
              <Typography variant="caption" color="text.secondary">{meta.isLoading ? "Loading…" : "No logo"}</Typography>
            )}
          </Box>
          {canEdit && (
            <>
              <input ref={input} type="file" accept="image/png,image/jpeg" hidden onChange={choose} data-testid="invoice-logo-input" />
              <Button variant="outlined" onClick={() => input.current?.click()} disabled={busy || meta.isError}>
                {has ? "Change logo" : "Add logo"}
              </Button>
              {has && (
                <Button color="error" onClick={remove} disabled={busy}>Remove</Button>
              )}
            </>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {canEdit
            ? "A PNG or JPEG, printed at the top of your invoices beside your name. Not your portal logo. Invoices already issued keep the logo they were issued with."
            : "Printed at the top of this provider's invoices. Only its Owner or Admin can change it."}
        </Typography>
      </Stack>
    </Paper>
  );
}
